/**
 * Help Center content — Part 1: Getting Started, Crew, Equipment.
 * Imported by helpContent/index.js; do not import directly.
 */

// ─── GETTING STARTED ─────────────────────────────────────────────────────────

export const gettingStarted = {
  id: 'getting-started',
  title: 'Getting Started',
  summary: 'Install the app, log in, find your way around, and set your vessel category.',
  keywords: ['install', 'home screen', 'login', 'sign in', 'dashboard', 'menu', 'vessel category', 'safari', 'chrome', 'android', 'iphone', 'setup'],
  steps: [
    {
      text: 'FleetSkipper runs in a web browser, but on a phone or tablet it\'s much better to install it to your home screen — it then opens full-screen like a normal app, works offline, and is one tap away. On a computer you can skip the install and just use the browser.',
      image: null,
    },
    {
      text: 'Install on iPhone or iPad (Safari only): Open app.fleetskipper.com in Safari. Tap the Share button (square with an arrow pointing up). Scroll down and tap Add to Home Screen. Confirm the FleetSkipper name and icon, then tap Add (top right). FleetSkipper now sits on your home screen.',
      image: null,
    },
    {
      text: 'Install on Android (Chrome): Open app.fleetskipper.com in Chrome. If an Install banner appears, tap it. Otherwise tap the ⋮ menu (three dots, top-right) and choose Install app or Add to Home screen. Tap Install / Add.',
      image: null,
    },
    {
      text: 'Log in: Open app.fleetskipper.com in your browser (Chrome or Safari work best). Enter your email and password, then tap Sign In.',
      image: '01-login-screen.png',
    },
    {
      text: 'You land on your Dashboard. Your vessel\'s name is shown top-left, and the menu down the left-hand side is how you move around the app. Tip: tick "Remember me for 30 days" so you\'re not logging in every time.',
      image: '01b-dashboard.png',
    },
    {
      text: 'Finding your way around: The left menu is your map — Crew, Equipment, Operations (voyages, checklists, logbook), Drills, Defects, Incidents, Risk Assessments, Docs & Compliance, Account, Admin Panel (super-admin only), and Settings. The vessel switcher (top-left) shows which boat you\'re working on. If you run more than one vessel, always check it says the right one before you add anything.',
      image: '01c-vessel-picker.png',
    },
    {
      text: 'The header shows a sync status ("Synced"). While it\'s saving it briefly reads "Syncing", then returns to "Synced" — that\'s your confirmation the work reached the cloud.',
      image: null,
    },
    {
      text: 'Set your vessel category FIRST — do this before anything else. Go to Settings → Vessel Settings. On the Classification step, choose your WBC3 Category (0–6) for the vessel.',
      image: 'cat-01-vessel-category-set.png',
    },
    {
      text: 'Tap Save. The app now asks for the correct certificates for your vessel throughout Crew, Hours of Rest and Voyages. Setting your vessel category first is the single most useful thing to get right early — the whole compliance engine keys off it.',
      image: null,
    },
  ],
};

// ─── 1. CREW ─────────────────────────────────────────────────────────────────

export const crew = {
  id: 'crew',
  title: '1. Crew',
  summary: 'Manage crew members, certificates, hours of rest, and familiarisation.',
  keywords: ['crew', 'certificates', 'competency', 'medical', 'stcw', 'master', 'skipper', 'pst', 'hours of rest', 'familiarisation', 'induction', 'compliance', 'archive', 'delete', 'export'],
  steps: [
    {
      text: 'The Crew module is where you manage the people on your vessel: their positions, contact details, emergency contacts, professional certificates and qualifications, hours of rest, and familiarisation (vessel induction). The app continuously checks each crew member against the certificate and rest requirements for your vessel\'s category, and flags anyone who is missing something or overdue. Open it from left menu → Crew.',
      image: 'crew-01-landing.png',
    },
    {
      text: 'Add a crew member: On Crew List, tap Add Crew Member. Only Full Name and Position are required — everything else is optional.',
      image: 'crew-02-add-form.png',
    },
    {
      text: 'Enter a Full Name, then open Position and choose (Skipper / Master / Mate / Engineer / Deckhand / Trainee / Crane Operator / …). When you pick Master, a Certificates & Qualifications requirements panel appears, driven by your vessel category.',
      image: 'crew-02c-position-master.png',
    },
    {
      text: 'Add certificates that qualify a Master: Tap Add Certificate. A "Search certificates..." box appears — type to filter the catalogue.',
      image: 'crew-03-add-cert.png',
    },
    {
      text: 'Pick STCW Master (Workboat <500GT) (or another accepted Certificate of Competency). Fill Certificate Number, Issued By, Issue Date, and Expiry Date, then tap the dialog\'s Add Certificate to attach it.',
      image: 'crew-03c-cert-search-master.png',
    },
    {
      text: 'Repeat to add a medical (search ML5 or ENG1) and Personal Survival Techniques (PST), each with an expiry date. These three certs are what make a Master voyage-qualified.',
      image: 'crew-04-certs-added.png',
    },
    {
      text: 'Tap Save Crew Member. They appear in the Crew List with their certificate count (e.g. Certs Valid (3)), and the "Missing: Certificate of Competency / PST / No medical" flags disappear — proving the compliance engine is reacting to real data.',
      image: 'crew-05-after-save.png',
    },
    {
      text: 'Click the member\'s name to open their Crew Profile. Sections include Basic Information, Emergency Contact, Certificates (with a Certificate Compliance % and Expired / Expiring Soon / Valid / Total tiles), Drill Participation, Training History and Hours of Rest.',
      image: 'crew-06-member-detail.png',
    },
    {
      text: 'Use Show more on Certificates to list each cert grouped by type, each showing "Expires in N days" and a ✓ Valid status.',
      image: 'crew-06b-certs-expanded.png',
    },
    {
      text: 'Tap Edit to change details — the form is titled Edit Crew Member and the bottom button reads Update Crew Member.',
      image: 'crew-07-edit.png',
    },
    {
      text: 'Archive hides a crew member from the main list but can be restored later (the reversible "deactivate"). Delete is permanent. From Quick Actions, tap Export List to download a CSV of your crew.',
      image: 'crew-13-archive-confirm.png',
    },
  ],
};

// ─── 2. EQUIPMENT ────────────────────────────────────────────────────────────

export const equipment = {
  id: 'equipment',
  title: '2. Equipment',
  summary: 'Your equipment register, planned maintenance, and maintenance history.',
  keywords: ['equipment', 'maintenance', 'register', 'template', 'job', 'safety check', 'defect', 'history', 'documents', 'photo', 'planned', 'overdue', 'compliance'],
  steps: [
    {
      text: 'The Equipment module is your equipment register plus a planned-maintenance system. You keep a list of every item aboard, schedule recurring maintenance jobs, carry them out and record the completion (which builds an audit history), and attach photos and documents to each item. It has three tabs: Planned Maintenance, Equipment Register, and Maintenance History.',
      image: '02-equipment-landing.png',
    },
    {
      text: 'Add an equipment item: Click Equipment Register, then Add Equipment. You can Choose from Templates (59) or Create from Scratch.',
      image: '03-equipment-register.png',
    },
    {
      text: 'Fill the three required fields — Equipment Name, Category, Location — and optionally Manufacturer / Model / Serial Number / Specifications / Notes. Tap Add Equipment. It appears in the register grouped under its category.',
      image: '04-add-equipment-form.png',
    },
    {
      text: 'Click an equipment name to open its detail view. The header shows the item, its tags (Source, category, location, an equipment code) and buttons Log defect and View maintenance. There are three sub-tabs: Details, History, Documents.',
      image: '05-item-detail.png',
    },
    {
      text: 'On Documents, attach photos (Take photo / Choose from gallery — "Photos sync across your devices") and upload documents via the dropzone (.pdf / .doc / .docx, max 2MB).',
      image: '06-item-documents.png',
    },
    {
      text: 'The History sub-tab shows maintenance and inspection records for that item (empty until you complete a job).',
      image: '07-item-history.png',
    },
    {
      text: 'Raise a maintenance job: From Planned Maintenance → Add Job. Set Item Name, Interval and Unit (Days / Weeks / Months / Years / Running Hours), pick a First Due Date option, and fill any Job Details. To tie the job to a specific piece of kit, scroll to Link to Equipment and tick the equipment item(s) from your register. Tap Save Job.',
      image: '08-add-job-form.png',
    },
    {
      text: 'Click a job card to open its detail — Job Description, Evidence Photos, a green Complete Safety Check button, a Delete Job button, and a Safety Check History table.',
      image: '09-job-detail.png',
    },
    {
      text: 'Click Complete Safety Check. An inline "Complete Check" panel opens. Set Date Completed (pre-filled with today), choose Completed By, leave All checks completed ticked (or untick if not), add any General Notes, then tap Complete Check. A new row appears in the Safety Check History and the item\'s Last Service Date updates automatically.',
      image: 'live-complete-form.png',
    },
  ],
};
